import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, History as HistoryIcon, TrendingDown, TrendingUp, Cog, Search, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import StockThumb from "@/components/StockThumb";
import { packSummary, fmtQty } from "@/lib/pack";
import { DepartmentBadge } from "@/components/DepartmentChips";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const PAGE = 200;

// v211 — CoC chip; managers can add/fix a missed CoC on a received row.
const CocChip = ({ m, canEdit, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(m.batch_ref || "");
  const save = async () => {
    try {
      const { data } = await api.put(`/movements/${m.id}`, { batch_ref: val });
      onSaved(data);
      setEditing(false);
      toast.success("CoC saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save CoC");
    }
  };
  if (editing)
    return (
      <span className="inline-flex items-center gap-1">
        <input
          autoFocus
          data-testid={`hist-batch-input-${m.id}`}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()}
          placeholder="CoC / Batch ref"
          className="nb-input py-0.5 px-1.5 text-[11px] w-44"
        />
        <button data-testid={`hist-batch-save-${m.id}`} onClick={save} className="nb-btn nb-btn-primary p-1">
          <Check size={11} strokeWidth={3} />
        </button>
        <button onClick={() => setEditing(false)} className="nb-btn bg-white p-1" aria-label="cancel">
          <X size={11} strokeWidth={3} />
        </button>
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1">
      {m.batch_ref ? (
        <span
          data-testid={`hist-batch-${m.id}`}
          className="font-mono-num text-[11px] bg-[#FFF8E1] border border-[#7A5300] text-[#7A5300] rounded px-1.5 py-0.5"
          title="CoC / Batch reference"
        >
          CoC: {m.batch_ref}
        </span>
      ) : (
        <span data-testid={`hist-no-coc-${m.id}`} className="text-[10px] bg-[#EEF1F5] text-[#607087] rounded px-1.5 py-0.5">
          No CoC recorded
        </span>
      )}
      {canEdit && (
        <button
          data-testid={`hist-batch-edit-${m.id}`}
          onClick={() => setEditing(true)}
          className="p-0.5 rounded hover:bg-black/5"
          title={m.batch_ref ? "Edit CoC" : "Add CoC"}
        >
          <Pencil size={11} strokeWidth={2.5} />
        </button>
      )}
    </span>
  );
};

const ItemHistoryDialog = ({ open, item, onClose }) => {
  const { isManager } = useAuth();
  const [movs, setMovs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [more, setMore] = useState(false);
  const [q, setQ] = useState("");

  const load = (skip, query) => {
    setLoading(true);
    return api
      .get("/movements", { params: { stock_id: item.id, limit: PAGE, skip, q: query || undefined } })
      .then((r) => {
        setMovs((prev) => (skip ? [...prev, ...r.data] : r.data));
        setMore(r.data.length === PAGE);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !item?.id) return;
    const t = setTimeout(() => load(0, q.trim()), q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item, q]);

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const patchMov = (upd) => setMovs((rs) => rs.map((r) => (r.id === upd.id ? { ...r, batch_ref: upd.batch_ref } : r)));

  const [totals, setTotals] = useState({ in: 0, out: 0 });
  useEffect(() => {
    if (q) return;
    setTotals({
      out: movs.filter((m) => m.action === "OUT").reduce((s, m) => s + m.qty, 0),
      in: movs.filter((m) => m.action === "IN").reduce((s, m) => s + m.qty, 0),
    });
  }, [movs, q]);
  if (!open || !item) return null;


  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="item-history-overlay"
    >
      <div
        className="nb-card p-6 w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="item-history-dialog"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <StockThumb item={item} size="h-16 w-16" />
            <div>
              <div className="label-eyebrow">Item history</div>
              <h3 className="font-display text-2xl mt-1">{item.name}</h3>
              <div className="text-xs text-[#6B6B6B] mt-1">
                {item.type} · {item.size_spec || item.sku || ""}
              </div>
              {packSummary(item) && (
                <div
                  className="text-xs font-medium text-[#9A6F00] mt-1"
                  data-testid="item-history-pack-summary"
                >
                  {packSummary(item)}
                </div>
              )}
            </div>
          </div>
          <button
            data-testid="item-history-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="nb-card-sm p-3">
            <div className="label-eyebrow">Current</div>
            <div className="font-mono-num font-bold text-2xl">
              {fmtQty(item.current_qty)} <span className="text-sm text-[#6B6B6B]">{item.unit}</span>
            </div>
          </div>
          <div className="nb-card-sm p-3" style={{ background: "#DFEEF7" }}>
            <div className="label-eyebrow flex items-center gap-1">
              <TrendingUp size={12} strokeWidth={2.5} /> Received
            </div>
            <div className="font-mono-num font-bold text-2xl">
              +{fmtQty(totals.in)} <span className="text-sm text-[#607087]">{item.unit}</span>
            </div>
          </div>
          <div className="nb-card-sm p-3" style={{ background: "#FFF1EE" }}>
            <div className="label-eyebrow flex items-center gap-1">
              <TrendingDown size={12} strokeWidth={2.5} /> Taken
            </div>
            <div className="font-mono-num font-bold text-2xl">
              −{fmtQty(totals.out)} <span className="text-sm text-[#607087]">{item.unit}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <HistoryIcon size={18} strokeWidth={2.5} />
          <div className="label-eyebrow">Movements</div>
          <div className="relative ml-auto w-full sm:w-64">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#607087]" />
            <input
              data-testid="item-history-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search CoC, PO, notes, person…"
              className="nb-input py-1.5 pl-8 text-sm"
            />
          </div>
        </div>

        <div
          className="flex-1 overflow-y-auto scrollbar-clean border-2 border-black rounded-xl"
          data-testid="item-history-list"
        >
          {loading && movs.length === 0 ? (
            <div className="p-8 text-center text-[#6B6B6B] text-sm">Loading…</div>
          ) : movs.length === 0 ? (
            <div className="p-8 text-center text-[#6B6B6B] text-sm">
              {q ? "No movements match your search." : "No movements yet for this item."}
            </div>
          ) : (
            movs.map((m) => (
              <div
                key={m.id}
                className="grid grid-cols-12 gap-2 p-3 border-b border-black/10 items-start"
                data-testid={`hist-row-${m.id}`}
              >
                <div className="col-span-4 font-mono-num text-xs">{fmt(m.created_at)}</div>
                <div className="col-span-2">
                  <span
                    className="text-[11px] tracking-wide font-bold px-2 py-0.5 rounded"
                    style={{
                      background: m.action === "OUT" ? "#228EBF" : "#5FBE7E",
                      color: "#fff",
                    }}
                  >
                    {m.action}
                  </span>
                </div>
                <div className="col-span-2 font-mono-num font-bold">
                  {m.sub_qty && m.sub_unit ? (
                    <>
                      {fmtQty(m.sub_qty)} {m.sub_unit}
                      <div className="text-[10px] text-[#607087] font-normal">
                        ({fmtQty(m.qty)} {m.unit})
                      </div>
                    </>
                  ) : (
                    <>
                      {fmtQty(m.qty)} {m.unit}
                    </>
                  )}
                </div>
                <div className="col-span-2 text-sm truncate">
                  <div className="truncate">{m.user_name}</div>
                  {m.department && (
                    <div className="mt-0.5" data-testid={`hist-dept-${m.id}`}>
                      <DepartmentBadge department={m.department} />
                    </div>
                  )}
                </div>
                <div className="col-span-2 text-xs text-[#6B6B6B] min-w-0">
                  {m.requester && (
                    <div className="truncate" title={m.requester}>← {m.requester}</div>
                  )}
                  {m.supplier && (
                    <div className="truncate" title={m.supplier}>{m.supplier}</div>
                  )}
                  {m.po_number && (
                    <div
                      className="truncate font-bold text-[#0A3A7A]"
                      title={m.po_number}
                      data-testid={`hist-po-${m.id}`}
                    >
                      {m.po_number}
                    </div>
                  )}
                </div>
                {/* v204 — Full-width Batch / CoC + Notes row — only
                    renders when either is populated, so legacy OUT
                    moves stay compact. Searching by CoC number works
                    straight away because this surfaces every stored
                    batch_ref inline. */}
                {(m.batch_ref || m.notes || m.applied_machines?.length || (m.action === "IN" && !m.opening && !m.stocktake)) && (
                  <div className="col-span-12 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 pt-1.5 border-t border-black/5 mt-1">
                    {(m.applied_machines || []).map((am) => (
                      <span
                        key={am.id}
                        className="inline-flex items-center gap-1 text-[11px] font-bold bg-[#EAF5FB] border border-[#0A3A7A] text-[#0A3A7A] rounded px-1.5 py-0.5"
                        data-testid={`hist-machine-${m.id}-${am.id}`}
                      >
                        <Cog size={11} strokeWidth={2.5} /> Used on {am.name}
                      </span>
                    ))}
                    {(m.batch_ref || (m.action === "IN" && !m.opening && !m.stocktake)) && (
                      <CocChip m={m} canEdit={isManager && m.action === "IN"} onSaved={patchMov} />
                    )}
                    {m.notes && (
                      <span
                        className="text-[11px] text-[#4A5666] italic break-words min-w-0"
                        title={m.notes}
                        data-testid={`hist-notes-${m.id}`}
                      >
                        {m.notes}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
          {more && (
            <button
              data-testid="item-history-load-older"
              onClick={() => load(movs.length, q.trim())}
              disabled={loading}
              className="w-full p-3 text-sm font-bold text-[#0A3A7A] hover:bg-[#EAF5FB]"
            >
              {loading ? "Loading…" : "Load older movements"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemHistoryDialog;
